import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, TrendingUp, TrendingDown, Scale, FileText, Plus,
  Trash2, Download, Settings2, X, Wallet, Building2, User, Zap, Lock, Receipt,
  Percent, Paperclip, Unlock, Pencil, Check,
} from "lucide-react";
import { booksApi } from "@/api/books";
import { downloadAsset } from "@/api/mist";

const money = (sym, v) => `${sym || "$"}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[8px] font-black uppercase tracking-widest text-white/50">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function TaxBooksPage({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [jur, setJur] = useState(null);
  const [taxYears, setTaxYears] = useState([]);
  const [taxYear, setTaxYear] = useState("");
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [section, setSection] = useState("income");
  const [sync, setSync] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [jurList, setJurList] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [busy, setBusy] = useState(false);

  const sym = summary?.currency_symbol || jur?.currency_symbol || "$";
  const hasGst = !!jur?.has_gst_vat;
  const sections = [
    { key: "income", label: "Income", Icon: TrendingUp },
    { key: "expenses", label: "Expenses", Icon: TrendingDown },
    ...(hasGst ? [{ key: "gst", label: jur?.sales_tax_label || "GST", Icon: Percent }] : []),
    { key: "liability", label: "Tax", Icon: Scale },
    { key: "documents", label: "Docs", Icon: FileText },
  ];

  const loadCore = useCallback(async () => {
    const s = await booksApi.settings();
    setSettings(s.settings);
    setJur(s.jurisdiction);
    setTaxYears(s.tax_years);
    setTaxYear((prev) => prev || s.current_tax_year);
    return s;
  }, []);

  const refresh = useCallback(async (ty) => {
    const year = ty || taxYear;
    if (!year) return;
    const [sum, ent, docs] = await Promise.all([
      booksApi.summary(year),
      booksApi.entries({ tax_year: year }),
      booksApi.documents(year),
    ]);
    setSummary(sum);
    setEntries(ent.entries || []);
    setDocuments(docs.documents || []);
  }, [taxYear]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = await loadCore();
        await Promise.all([
          refresh(s.current_tax_year),
          booksApi.syncStatus().then(setSync),
          booksApi.jurisdictions().then((d) => setJurList(d.jurisdictions || [])),
        ]);
      } catch { toast.error("Could not load your books"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (taxYear && !loading) refresh(taxYear); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxYear]);
  useEffect(() => { if (section === "gst" && !hasGst) setSection("income"); }, [hasGst, section]);

  const exportPdf = async () => {
    setBusy(true);
    try {
      const r = await booksApi.exportDocument(taxYear);
      toast.success("Itemised PDF generated ✓");
      await downloadAsset(r.asset_id, `tax_books_${taxYear}`, "pdf");
      const docs = await booksApi.documents(taxYear);
      setDocuments(docs.documents || []);
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    try { await booksApi.deleteEntry(id); toast("Entry archived (retained for records)"); await refresh(); }
    catch (e) {
      if (e?.response?.status === 409) toast.error("Under legal hold — release it first");
      else toast.error("Could not remove entry");
    }
  };
  const toggleHold = async (id, hold) => {
    try { await booksApi.setLegalHold(id, hold); toast(hold ? "Legal hold applied 🔒" : "Legal hold released"); await refresh(); }
    catch { toast.error("Could not update legal hold"); }
  };
  const [syncBusy, setSyncBusy] = useState(false);
  const vaultSyncNow = async () => {
    setSyncBusy(true);
    try {
      const r = await booksApi.vaultSync();
      const imp = r.imported || {};
      toast.success(`Imported ${imp.bank || 0} bank · ${imp.receipts || 0} receipts${r.deduped ? ` · ${r.deduped} skipped` : ""}`);
      await refresh();
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Automated sync pending legal approval");
      else toast.error("Vault sync failed");
    } finally { setSyncBusy(false); }
  };
  const toggleLegalApproval = async (approved) => {
    try {
      await booksApi.setLegalApproval(approved);
      toast.success(approved ? "Automated sync unlocked for all users ✓" : "Automated sync locked");
      setSync(await booksApi.syncStatus());
    } catch { toast.error("Could not update approval"); }
  };

  if (loading) {
    return (
      <div className="absolute inset-0 z-30 grid place-items-center bg-slate-950" data-testid="taxbooks-loading">
        <Loader2 size={26} className="text-gold animate-spin" />
      </div>
    );
  }

  const lb = summary?.liability || {};

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-slate-950 overflow-hidden" data-testid="taxbooks-page">
      {/* header */}
      <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b-2 border-white/10 bg-charcoal">
        <button onClick={onBack} data-testid="taxbooks-back" className="w-9 h-9 grid place-items-center rounded-lg border-2 border-gold/60 bg-white/5 active:scale-95"><ArrowLeft size={16} className="text-gold" /></button>
        <div className="flex-1 leading-none">
          <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-gold">Tax &amp; Books</p>
          <p className="font-serif text-lg text-white">{jur?.name} · {jur?.authority}</p>
        </div>
        <select value={taxYear} onChange={(e) => setTaxYear(e.target.value)} data-testid="taxbooks-year-select"
          className="bg-white/10 border-2 border-gold/50 rounded-lg px-2 py-1.5 font-mono text-[10px] font-black text-white outline-none focus:ring-2 focus:ring-gold">
          {taxYears.map((y) => <option key={y} value={y} className="bg-charcoal">{y}</option>)}
        </select>
        <button onClick={() => setShowSettings(true)} data-testid="taxbooks-settings-btn" className="w-9 h-9 grid place-items-center rounded-lg border-2 border-white/30 bg-white/5 active:scale-95"><Settings2 size={16} className="text-white/70" /></button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        {/* liability hero */}
        <div className="border-4 border-charcoal rounded-[24px] bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="taxbooks-liability-card">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] font-black uppercase tracking-[0.2em] text-charcoal">Estimated Tax · FY {taxYear}</p>
            <span className="font-mono text-[8px] uppercase tracking-widest text-charcoal/40">{settings?.entity_type === "company" ? "Company" : "Sole Trader"}</span>
          </div>
          <p className="mt-1.5 font-serif text-4xl text-charcoal tabular-nums" data-testid="taxbooks-total-liability">{money(sym, lb.total_liability)}</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Stat label="Taxable income" value={money(sym, lb.taxable_income)} testid="taxbooks-taxable" />
            <Stat label="Income tax" value={money(sym, lb.income_tax)} />
            <Stat label="Eff. rate" value={`${((lb.effective_rate || 0) * 100).toFixed(1)}%`} />
          </div>
          {typeof lb.estimated_position === "number" && lb.payg_withheld > 0 && (
            <div className={`mt-2 rounded-xl border-2 px-3 py-2 font-mono text-[10px] font-black ${lb.estimated_position >= 0 ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-rose-500 text-rose-700 bg-rose-50"}`} data-testid="taxbooks-position">
              {lb.estimated_position >= 0 ? "Estimated refund" : "Estimated payable"}: {money(sym, Math.abs(lb.estimated_position))}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between font-mono text-[9px] text-charcoal/60">
            <span className="flex items-center gap-1"><TrendingUp size={11} className="text-emerald-600" /> {money(sym, summary?.income_total)}</span>
            <span className="flex items-center gap-1"><TrendingDown size={11} className="text-rose-600" /> {money(sym, summary?.expense_total)}</span>
            <span className="flex items-center gap-1"><Wallet size={11} /> Net {money(sym, summary?.net)}</span>
          </div>
          <p className="mt-2 font-mono text-[7.5px] uppercase tracking-widest text-charcoal/35">{lb.disclaimer || "Estimate only — not tax advice."}</p>
        </div>

        {/* automated sync banner */}
        {sync && (
          <div className={`rounded-2xl border-2 px-3 py-2.5 ${sync.available ? "border-gold/60 bg-gold/10" : "border-white/15 bg-white/5"}`} data-testid="taxbooks-sync-banner">
            <div className="flex items-center gap-2">
              {sync.available ? <Zap size={14} className="text-gold" /> : <Lock size={14} className="text-white/50" />}
              <p className="flex-1 font-mono text-[9px] font-black uppercase tracking-widest text-white/80">{sync.available ? "Automated Sync · Live" : "Automated Sync · Coming Soon"}</p>
            </div>
            <p className="mt-1 font-mono text-[9px] leading-snug text-white/50">{sync.note}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {sync.channels.map((c) => (
                <span key={c.key} data-testid={`taxbooks-channel-${c.key}`} className={`font-mono text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full border-2 ${c.live ? "border-gold text-gold" : "border-white/20 text-white/40"}`}>{c.label}{c.live ? " · on" : ""}</span>
              ))}
            </div>
            {sync.available && (
              <button onClick={vaultSyncNow} disabled={syncBusy} data-testid="taxbooks-vault-sync-btn"
                className="mt-2.5 w-full flex items-center justify-center gap-2 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
                {syncBusy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Sync from Secure Vault
              </button>
            )}
            {sync.founder && (
              <button onClick={() => toggleLegalApproval(!sync.global_legal_approved)} data-testid="taxbooks-legal-approval-toggle"
                className="mt-2 w-full flex items-center gap-2 rounded-lg border-2 border-white/15 bg-white/5 px-3 py-2">
                <span className={`w-9 h-5 border-2 border-black flex items-center px-0.5 ${sync.global_legal_approved ? "bg-gold justify-end" : "bg-white/10 justify-start"}`}><span className="w-3.5 h-3.5 bg-charcoal border border-black" /></span>
                <span className="flex-1 text-left font-mono text-[8.5px] font-black uppercase tracking-widest text-white/75">Unlock automated sync for all users (legal approved)</span>
              </button>
            )}
          </div>
        )}

        {/* section tabs */}
        <div className={`grid gap-1.5 ${sections.length === 5 ? "grid-cols-5" : "grid-cols-4"}`} data-testid="taxbooks-tabs">
          {sections.map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)} data-testid={`taxbooks-tab-${s.key}`}
              className={`flex flex-col items-center gap-1 rounded-xl border-2 py-2 ${section === s.key ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>
              <s.Icon size={15} /><span className="font-mono text-[7.5px] font-black uppercase tracking-wide">{s.label}</span>
            </button>
          ))}
        </div>

        {section === "income" && (
          <EntrySection direction="income" jur={jur} sym={sym} entries={entries.filter((e) => e.direction === "income")}
            onChanged={() => refresh()} onRemove={removeEntry} onHold={toggleHold} />
        )}
        {section === "expenses" && (
          <EntrySection direction="expense" jur={jur} sym={sym} entries={entries.filter((e) => e.direction === "expense")}
            onChanged={() => refresh()} onRemove={removeEntry} onHold={toggleHold} />
        )}
        {section === "gst" && hasGst && (
          <GstSection taxYear={taxYear} label={jur?.sales_tax_label || "GST"} sym={sym} />
        )}
        {section === "liability" && summary && (
          <LiabilityBreakdown summary={summary} sym={sym} />
        )}
        {section === "documents" && (
          <DocumentsSection sym={sym} documents={documents} busy={busy} onExport={exportPdf} note={jur?.compliance_note} />
        )}
      </div>

      {showSettings && (
        <SettingsSheet settings={settings} jur={jur} jurList={jurList} onClose={() => setShowSettings(false)}
          onSaved={async () => { setShowSettings(false); const s = await loadCore(); setTaxYear(s.current_tax_year); await refresh(s.current_tax_year); }} />
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border-2 border-charcoal/15 bg-cream px-2 py-2" data-testid={testid}>
      <p className="font-mono text-[7px] font-black uppercase tracking-widest text-charcoal/45">{label}</p>
      <p className="mt-0.5 font-mono text-[12px] font-black text-charcoal tabular-nums">{value}</p>
    </div>
  );
}

function EntrySection({ direction, jur, sym, entries, onChanged, onRemove, onHold }) {
  const cats = direction === "income" ? jur.income_categories : jur.expense_categories;
  const [entity, setEntity] = useState("personal");
  const [category, setCategory] = useState(cats[0]?.key || "other");
  const [amount, setAmount] = useState("");
  const [gstAmount, setGstAmount] = useState("");
  const [taxWithheld, setTaxWithheld] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [deductible, setDeductible] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isPayg = direction === "income" && category === "payg";
  const showGst = !!jur.has_gst_vat;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await booksApi.addEntry({
        entity, direction, category, amount: amt, date, note,
        ...(showGst ? { gst_amount: parseFloat(gstAmount) || 0 } : {}),
        ...(isPayg ? { tax_withheld: parseFloat(taxWithheld) || 0 } : {}),
        ...(direction === "expense" ? { deductible } : {}),
      });
      toast.success(direction === "income" ? "Income added ✓" : "Expense added ✓");
      setAmount(""); setGstAmount(""); setTaxWithheld(""); setNote("");
      onChanged();
    } catch { toast.error("Could not save entry"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`taxbooks-section-${direction}`}>
      {/* add form */}
      <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3 space-y-2.5" data-testid={`taxbooks-add-${direction}`}>
        <div className="grid grid-cols-2 gap-1.5">
          {["personal", "business"].map((en) => (
            <button key={en} onClick={() => setEntity(en)} data-testid={`taxbooks-entity-${en}`}
              className={`flex items-center justify-center gap-1.5 rounded-lg border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${entity === en ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>
              {en === "business" ? <Building2 size={12} /> : <User size={12} />} {en}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`taxbooks-cats-${direction}`}>
          {cats.map((c) => (
            <button key={c.key} onClick={() => { setCategory(c.key); if (direction === "expense") setDeductible(c.deductible_default); }} data-testid={`taxbooks-cat-${c.key}`}
              className={`font-mono text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full border-2 ${category === c.key ? "bg-gold text-black border-gold" : "bg-white/5 text-white/55 border-white/15"}`}>{c.label}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Amount (${sym})`}>
            <input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="taxbooks-amount"
              placeholder="0.00" className="w-full bg-white/10 border-2 border-gold/50 rounded-lg px-2.5 py-2 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold" />
          </Field>
          <Field label="Date">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="taxbooks-date"
              className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-[11px] text-white outline-none focus:ring-2 focus:ring-gold" />
          </Field>
        </div>
        {showGst && (
          <Field label={`${jur.sales_tax_label || "GST"} amount (${sym}) — optional`}>
            <input type="number" inputMode="decimal" value={gstAmount} onChange={(e) => setGstAmount(e.target.value)} data-testid="taxbooks-gst-amount"
              placeholder="0.00" className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold" />
          </Field>
        )}
        {isPayg && (
          <Field label={`PAYG tax withheld (${sym})`}>
            <input type="number" inputMode="decimal" value={taxWithheld} onChange={(e) => setTaxWithheld(e.target.value)} data-testid="taxbooks-withheld"
              placeholder="0.00" className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold" />
          </Field>
        )}
        {direction === "expense" && (
          <button onClick={() => setDeductible((v) => !v)} data-testid="taxbooks-deductible-toggle"
            className="w-full flex items-center gap-2 rounded-lg border-2 border-white/15 bg-white/5 px-3 py-2">
            <span className={`w-9 h-5 border-2 border-black flex items-center px-0.5 ${deductible ? "bg-gold justify-end" : "bg-white/10 justify-start"}`}><span className="w-3.5 h-3.5 bg-charcoal border border-black" /></span>
            <span className="flex-1 text-left font-mono text-[9px] font-black uppercase tracking-widest text-white/75">Tax deductible</span>
          </button>
        )}
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="taxbooks-note" placeholder="Note (optional)"
          className="w-full bg-white/10 border-2 border-white/15 rounded-lg px-2.5 py-2 font-mono text-[11px] text-white outline-none focus:ring-2 focus:ring-gold" />
        <button onClick={submit} disabled={busy} data-testid={`taxbooks-add-${direction}-btn`}
          className="w-full flex items-center justify-center gap-2 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add {direction}
        </button>
      </div>

      {/* list */}
      <div className="space-y-1.5" data-testid={`taxbooks-list-${direction}`}>
        {entries.length === 0 ? (
          <p className="text-center font-mono text-[10px] text-white/40 py-6">No {direction} entries for this year yet.</p>
        ) : entries.map((e) => (
          <EntryRow key={e.id} e={e} sym={sym} direction={direction} jur={jur} onChanged={onChanged} onRemove={onRemove} onHold={onHold} />
        ))}
      </div>
    </div>
  );
}

function EntryRow({ e, sym, direction, jur, onChanged, onRemove, onHold }) {
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const hasAttach = !!(e.attachment && e.attachment.vault_asset_id);
  const onFile = async (ev) => {
    const f = ev.target.files?.[0]; ev.target.value = "";
    if (!f) return;
    setUploading(true);
    try { await booksApi.uploadAttachment(e.id, f); toast.success("Receipt attached ✓"); onChanged(); }
    catch { toast.error("Attach failed (max 15MB)"); }
    finally { setUploading(false); }
  };

  if (editing) {
    return <EntryEdit e={e} sym={sym} direction={direction} jur={jur}
      onCancel={() => setEditing(false)}
      onSaved={() => { setEditing(false); onChanged(); }} />;
  }

  return (
    <div data-testid={`taxbooks-entry-${e.id}`} className="flex items-center gap-2 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5">
      <span className={`w-8 h-8 rounded-lg grid place-items-center border-2 ${e.entity === "business" ? "border-sky-400/60 text-sky-300" : "border-white/25 text-white/60"}`}>
        {e.entity === "business" ? <Building2 size={13} /> : <User size={13} />}
      </span>
      <div className="flex-1 min-w-0 leading-tight">
        <p className="font-mono text-[11px] font-black text-white truncate flex items-center gap-1">
          {e.legal_hold && <Lock size={9} className="text-gold shrink-0" />}{e.label}
        </p>
        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40">
          {e.date} · {e.entity}{direction === "expense" && e.deductible ? " · deductible" : ""}{e.gst_amount ? ` · gst ${sym}${e.gst_amount}` : ""}{e.source && e.source !== "manual" ? ` · ${e.source}` : ""}
        </p>
      </div>
      <span className={`font-mono text-[12px] font-black tabular-nums ${direction === "income" ? "text-emerald-400" : "text-rose-400"}`}>{money(sym, e.amount)}</span>
      {/* edit entry */}
      <button onClick={() => setEditing(true)} disabled={e.legal_hold} data-testid={`taxbooks-edit-${e.id}`}
        className="w-7 h-7 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 active:scale-90 disabled:opacity-30" title={e.legal_hold ? "Release legal hold to edit" : "Edit entry"}><Pencil size={12} className="text-white/60" /></button>
      {/* attach receipt */}
      {hasAttach ? (
        <button onClick={() => downloadAsset(e.attachment.vault_asset_id, e.attachment.filename?.replace(/\.[^.]+$/, "") || "receipt", (e.attachment.mime_type || "").includes("pdf") ? "pdf" : "png")}
          data-testid={`taxbooks-view-attach-${e.id}`} className="w-7 h-7 grid place-items-center rounded-lg border-2 border-gold/60 bg-gold/10 active:scale-90" title="View receipt"><Paperclip size={12} className="text-gold" /></button>
      ) : (
        <label data-testid={`taxbooks-attach-${e.id}`} className="w-7 h-7 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 active:scale-90 cursor-pointer" title="Attach receipt">
          {uploading ? <Loader2 size={12} className="text-white/60 animate-spin" /> : <Paperclip size={12} className="text-white/40" />}
          <input type="file" accept="image/*,application/pdf" hidden onChange={onFile} />
        </label>
      )}
      {/* legal hold */}
      <button onClick={() => onHold(e.id, !e.legal_hold)} data-testid={`taxbooks-hold-${e.id}`}
        className={`w-7 h-7 grid place-items-center rounded-lg border-2 active:scale-90 ${e.legal_hold ? "border-gold bg-gold/10" : "border-white/15 bg-white/5"}`} title={e.legal_hold ? "Release legal hold" : "Apply legal hold"}>
        {e.legal_hold ? <Lock size={12} className="text-gold" /> : <Unlock size={12} className="text-white/40" />}
      </button>
      {/* delete */}
      <button onClick={() => onRemove(e.id)} disabled={e.legal_hold} data-testid={`taxbooks-del-${e.id}`}
        className="w-7 h-7 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 active:scale-90 disabled:opacity-30"><Trash2 size={12} className="text-white/50" /></button>
    </div>
  );
}

function EntryEdit({ e, sym, direction, jur, onCancel, onSaved }) {
  const cats = direction === "income" ? jur.income_categories : jur.expense_categories;
  const [category, setCategory] = useState(e.category || cats[0]?.key || "other");
  const [entity, setEntity] = useState(e.entity || "personal");
  const [amount, setAmount] = useState(String(e.amount ?? ""));
  const [gstAmount, setGstAmount] = useState(String(e.gst_amount || ""));
  const [taxWithheld, setTaxWithheld] = useState(String(e.tax_withheld || ""));
  const [date, setDate] = useState((e.date || "").slice(0, 10));
  const [deductible, setDeductible] = useState(!!e.deductible);
  const [note, setNote] = useState(e.note || "");
  const [busy, setBusy] = useState(false);
  const isPayg = direction === "income" && category === "payg";
  const showGst = !!jur.has_gst_vat;

  const save = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await booksApi.updateEntry(e.id, {
        entity, direction, category, amount: amt, date, note,
        ...(showGst ? { gst_amount: parseFloat(gstAmount) || 0 } : {}),
        ...(isPayg ? { tax_withheld: parseFloat(taxWithheld) || 0 } : {}),
        ...(direction === "expense" ? { deductible } : {}),
      });
      toast.success("Entry updated ✓");
      onSaved();
    } catch { toast.error("Could not update entry"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3 space-y-2.5" data-testid={`taxbooks-edit-form-${e.id}`}>
      <div className="flex items-center gap-2">
        <Pencil size={13} className="text-gold" />
        <p className="flex-1 font-mono text-[9px] font-black uppercase tracking-widest text-gold">Edit {direction}</p>
        <button onClick={onCancel} data-testid={`taxbooks-edit-cancel-${e.id}`} className="w-7 h-7 grid place-items-center rounded-lg border-2 border-white/20 bg-white/5"><X size={13} className="text-white/70" /></button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {["personal", "business"].map((en) => (
          <button key={en} onClick={() => setEntity(en)} data-testid={`taxbooks-edit-entity-${en}`}
            className={`flex items-center justify-center gap-1.5 rounded-lg border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${entity === en ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>
            {en === "business" ? <Building2 size={12} /> : <User size={12} />} {en}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {cats.map((c) => (
          <button key={c.key} onClick={() => { setCategory(c.key); if (direction === "expense") setDeductible(c.deductible_default); }} data-testid={`taxbooks-edit-cat-${c.key}`}
            className={`font-mono text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full border-2 ${category === c.key ? "bg-gold text-black border-gold" : "bg-white/5 text-white/55 border-white/15"}`}>{c.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label={`Amount (${sym})`}>
          <input type="number" inputMode="decimal" value={amount} onChange={(ev) => setAmount(ev.target.value)} data-testid={`taxbooks-edit-amount-${e.id}`}
            placeholder="0.00" className="w-full bg-white/10 border-2 border-gold/50 rounded-lg px-2.5 py-2 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold" />
        </Field>
        <Field label="Date">
          <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} data-testid={`taxbooks-edit-date-${e.id}`}
            className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-[11px] text-white outline-none focus:ring-2 focus:ring-gold" />
        </Field>
      </div>
      {showGst && (
        <Field label={`${jur.sales_tax_label || "GST"} amount (${sym}) — optional`}>
          <input type="number" inputMode="decimal" value={gstAmount} onChange={(ev) => setGstAmount(ev.target.value)} data-testid={`taxbooks-edit-gst-${e.id}`}
            placeholder="0.00" className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold" />
        </Field>
      )}
      {isPayg && (
        <Field label={`PAYG tax withheld (${sym})`}>
          <input type="number" inputMode="decimal" value={taxWithheld} onChange={(ev) => setTaxWithheld(ev.target.value)} data-testid={`taxbooks-edit-withheld-${e.id}`}
            placeholder="0.00" className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold" />
        </Field>
      )}
      {direction === "expense" && (
        <button onClick={() => setDeductible((v) => !v)} data-testid={`taxbooks-edit-deductible-${e.id}`}
          className="w-full flex items-center gap-2 rounded-lg border-2 border-white/15 bg-white/5 px-3 py-2">
          <span className={`w-9 h-5 border-2 border-black flex items-center px-0.5 ${deductible ? "bg-gold justify-end" : "bg-white/10 justify-start"}`}><span className="w-3.5 h-3.5 bg-charcoal border border-black" /></span>
          <span className="flex-1 text-left font-mono text-[9px] font-black uppercase tracking-widest text-white/75">Tax deductible</span>
        </button>
      )}
      <input value={note} onChange={(ev) => setNote(ev.target.value)} data-testid={`taxbooks-edit-note-${e.id}`} placeholder="Note (optional)"
        className="w-full bg-white/10 border-2 border-white/15 rounded-lg px-2.5 py-2 font-mono text-[11px] text-white outline-none focus:ring-2 focus:ring-gold" />
      <button onClick={save} disabled={busy} data-testid={`taxbooks-edit-save-${e.id}`}
        className="w-full flex items-center justify-center gap-2 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save changes
      </button>
    </div>
  );
}

function GstSection({ taxYear, label, sym }) {
  const [quarter, setQuarter] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    booksApi.gst(taxYear, quarter).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [taxYear, quarter]);
  const codes = data?.reporting_codes || {};
  return (
    <div className="space-y-3" data-testid="taxbooks-section-gst">
      <div className="grid grid-cols-4 gap-1.5" data-testid="taxbooks-quarter-tabs">
        {[1, 2, 3, 4].map((q) => (
          <button key={q} onClick={() => setQuarter(q)} data-testid={`taxbooks-quarter-${q}`}
            className={`rounded-lg border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${quarter === q ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>Q{q}</button>
        ))}
      </div>
      {loading ? <div className="grid place-items-center py-8"><Loader2 size={20} className="text-gold animate-spin" /></div> : data && (
        <>
          <div className="border-4 border-charcoal rounded-[24px] bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="taxbooks-gst-card">
            <p className="font-mono text-[10px] font-black uppercase tracking-[0.2em] text-charcoal">{label} · {data.period_start} → {data.period_end}</p>
            <p className={`mt-1.5 font-serif text-4xl tabular-nums ${data.net_remittance > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="taxbooks-gst-net">{money(sym, Math.abs(data.net_remittance))}</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-charcoal/50">{data.settlement_direction === "PAYMENT_REQUIRED" ? `${label} payable` : `${label} refund`}</p>
          </div>
          <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3 space-y-1.5">
            <Row label="G1 · Total sales" value={money(sym, codes.G1_total_sales)} />
            <Row label={`1A · ${label} collected`} value={money(sym, codes["1A_gst_collected"])} />
            <Row label={`1B · ${label} credits`} value={money(sym, codes["1B_gst_credits"])} />
            <Row label="Net remittance" value={money(sym, data.net_remittance)} bold gold />
          </div>
          <p className="font-mono text-[8px] uppercase tracking-widest text-white/35 text-center">{data.disclaimer}</p>
        </>
      )}
    </div>
  );
}

function LiabilityBreakdown({ summary, sym }) {
  const lb = summary.liability;
  const ic = summary.income_by_class || {};
  const CLASS_LABELS = { payg: "PAYG Salary", business: "Business", crypto: "Crypto", shares: "Dividends/Shares", other: "Other" };
  return (
    <div className="space-y-3" data-testid="taxbooks-section-liability">
      <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3 space-y-1.5">
        <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1">Where your money came from</p>
        {Object.entries(ic).map(([k, v]) => (
          <Row key={k} label={CLASS_LABELS[k] || k} value={money(sym, v)} />
        ))}
        <Row label="Total income" value={money(sym, summary.income_total)} bold />
      </div>
      <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3 space-y-1.5">
        <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold mb-1">Assessment</p>
        <Row label="Gross income" value={money(sym, lb.gross_income)} />
        <Row label="Business net" value={money(sym, lb.business_net)} />
        <Row label="Deductions" value={money(sym, lb.deductions)} />
        <Row label="Taxable income" value={money(sym, lb.taxable_income)} bold />
        {lb.entity_type === "company" && <Row label={`Company tax (${Math.round((lb.company_rate || 0) * 100)}%)`} value={money(sym, lb.company_tax)} />}
        <Row label="Income tax" value={money(sym, lb.income_tax)} />
        {(lb.levies || []).map((l) => <Row key={l.key} label={l.label} value={money(sym, l.amount)} />)}
        <Row label="Total estimated liability" value={money(sym, lb.total_liability)} bold gold />
      </div>
      <p className="font-mono text-[8px] uppercase tracking-widest text-white/35 text-center">{lb.disclaimer}</p>
    </div>
  );
}

function Row({ label, value, bold, gold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`font-mono text-[10px] ${bold ? "font-black text-white" : "text-white/60"}`}>{label}</span>
      <span className={`font-mono text-[11px] tabular-nums ${gold ? "text-gold font-black" : bold ? "text-white font-black" : "text-white/70"}`}>{value}</span>
    </div>
  );
}

function DocumentsSection({ sym, documents, busy, onExport, note }) {
  return (
    <div className="space-y-3" data-testid="taxbooks-section-documents">
      <div className="border-2 border-gold/40 rounded-2xl bg-gold/5 p-3">
        <div className="flex items-center gap-2 mb-1"><Receipt size={15} className="text-gold" /><p className="font-mono text-[10px] font-black uppercase tracking-widest text-white">Download a tidy tax summary</p></div>
        <p className="font-mono text-[9px] text-white/50 leading-snug">Income &amp; expense schedules, deductible split, and a tax-liability estimate — ready to hand to a bookkeeper.</p>
        <button onClick={onExport} disabled={busy} data-testid="taxbooks-export-btn"
          className="mt-2.5 w-full flex items-center justify-center gap-2 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Generate &amp; Download PDF
        </button>
      </div>
      <div className="space-y-1.5" data-testid="taxbooks-docs-list">
        {documents.length === 0 ? (
          <p className="text-center font-mono text-[10px] text-white/40 py-4">No exports yet.</p>
        ) : documents.map((d) => (
          <button key={d.id} onClick={() => downloadAsset(d.asset_id, d.filename.replace(/\.pdf$/, ""), "pdf")} data-testid={`taxbooks-doc-${d.id}`}
            className="w-full flex items-center gap-2 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5 active:scale-[0.99]">
            <FileText size={15} className="text-gold shrink-0" />
            <div className="flex-1 min-w-0 text-left leading-tight">
              <p className="font-mono text-[10px] font-black text-white truncate">FY {d.tax_year}</p>
              <p className="font-mono text-[8px] uppercase tracking-widest text-white/40">{(d.generated_at || "").slice(0, 10)}</p>
            </div>
            <Download size={14} className="text-white/50" />
          </button>
        ))}
      </div>
      {note && <p className="font-mono text-[8px] uppercase tracking-widest text-white/35 text-center">{note}</p>}
    </div>
  );
}

function SettingsSheet({ settings, jur, jurList, onClose, onSaved }) {
  const [country, setCountry] = useState(settings.country);
  const [entityType, setEntityType] = useState(settings.entity_type || "sole_trader");
  const [businessName, setBusinessName] = useState(settings.business_name || "");
  const [identifier, setIdentifier] = useState(settings.identifier || "");
  const [gst, setGst] = useState(!!settings.gst_registered);
  const [busy, setBusy] = useState(false);
  const activeJur = (jurList || []).find((j) => j.code === country) || jur;

  const save = async () => {
    setBusy(true);
    try {
      await booksApi.saveSettings({ country, entity_type: entityType, business_name: businessName, identifier, gst_registered: gst });
      toast.success("Settings saved ✓");
      onSaved();
    } catch { toast.error("Could not save settings"); }
    finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-40 bg-black/70 flex items-end" data-testid="taxbooks-settings-sheet" onClick={onClose}>
      <div className="w-full bg-charcoal border-t-4 border-black rounded-t-3xl p-5 space-y-3 max-h-[85%] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 pb-2 border-b-2 border-white/10">
          <Settings2 size={16} className="text-gold" /><h3 className="flex-1 font-serif text-xl text-white">Books Settings</h3>
          <button onClick={onClose} data-testid="taxbooks-settings-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/20 bg-white/5"><X size={15} className="text-white" /></button>
        </div>
        <Field label="Jurisdiction / Country">
          <div className="grid grid-cols-3 gap-1.5" data-testid="taxbooks-country-select">
            {(jurList || []).map((j) => (
              <button key={j.code} onClick={() => setCountry(j.code)} data-testid={`taxbooks-country-${j.code}`}
                className={`rounded-lg border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${country === j.code ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>
                {j.code}<span className="block text-[7px] font-normal normal-case tracking-normal opacity-70">{j.authority}</span>
              </button>
            ))}
          </div>
          <p className="mt-1 font-mono text-[8px] text-white/40">{activeJur?.currency} · {activeJur?.record_retention_years}-yr retention{activeJur?.has_gst_vat ? ` · ${activeJur?.sales_tax_label}` : " · no sales tax"}</p>
        </Field>
        <Field label="Entity type">
          <div className="grid grid-cols-2 gap-1.5">
            {[["sole_trader", "Sole Trader"], ["company", "Company"]].map(([k, l]) => (
              <button key={k} onClick={() => setEntityType(k)} data-testid={`taxbooks-entity-type-${k}`}
                className={`rounded-lg border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${entityType === k ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Business name (optional)">
          <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="taxbooks-business-name"
            className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-[11px] text-white outline-none focus:ring-2 focus:ring-gold" />
        </Field>
        <Field label={`${activeJur?.identifier_label || "ABN"} (optional)`}>
          <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="taxbooks-identifier"
            className="w-full bg-white/10 border-2 border-white/20 rounded-lg px-2.5 py-2 font-mono text-[11px] text-white outline-none focus:ring-2 focus:ring-gold" />
        </Field>
        {activeJur?.has_gst_vat && (
          <button onClick={() => setGst((v) => !v)} data-testid="taxbooks-gst-toggle" className="w-full flex items-center gap-2 rounded-lg border-2 border-white/15 bg-white/5 px-3 py-2">
            <span className={`w-9 h-5 border-2 border-black flex items-center px-0.5 ${gst ? "bg-gold justify-end" : "bg-white/10 justify-start"}`}><span className="w-3.5 h-3.5 bg-charcoal border border-black" /></span>
            <span className="flex-1 text-left font-mono text-[9px] font-black uppercase tracking-widest text-white/75">Registered for {activeJur?.sales_tax_label || "GST"}</span>
          </button>
        )}
        <button onClick={save} disabled={busy} data-testid="taxbooks-settings-save"
          className="w-full flex items-center justify-center gap-2 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save settings
        </button>
      </div>
    </div>
  );
}
